import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const ACCENT = "#FF4B2B";
const INACTIVE = "#D1D1D6";
const ROW_H = 34;
const SVG_W = 240;
const HUB_SIZE = 40;
const MIN_ACTIVE_W = 6;
const MAX_LINK_W = 24;
const INACTIVE_W = 2.5;

const buildLinkPath = (x0, y0, x1, y1) => {
  const midX = x0 + (x1 - x0) * 0.52;
  return `M ${x0} ${y0} C ${midX} ${y0}, ${midX} ${y1}, ${x1} ${y1}`;
};

const ManufacturerSankeyCard = ({
  items = [],
  loading = false,
  delay = "",
  onItemClick,
  className,
  fillParent = false,
}) => {
  const [hoveredKey, setHoveredKey] = useState(null);

  const rows = useMemo(() => {
    const list = (Array.isArray(items) ? items : []).map((item) => ({
      key: item?.key ?? item?.title,
      title: item?.title ?? "Unknown",
      value: Number(item?.value) || 0,
    }));
    return [...list].sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return String(a.title).localeCompare(String(b.title));
    });
  }, [items]);

  const total = rows.reduce((sum, row) => sum + (row.value ?? 0), 0);
  const maxValue = rows.reduce((max, row) => Math.max(max, row.value ?? 0), 0);

  const layout = useMemo(() => {
    const n = rows.length;
    const height = n * ROW_H;
    const hubX = 8;
    const hubY = height / 2 - HUB_SIZE / 2;
    const linkStartX = hubX + HUB_SIZE;
    const linkEndX = SVG_W - 2;

    const visualWeights = rows.map((row) => {
      if (total <= 0) return 1;
      if (row.value <= 0) return 0.35;
      return row.value;
    });
    const weightSum = visualWeights.reduce((s, w) => s + w, 0) || 1;
    const stackH = HUB_SIZE - 8;
    let acc = 0;

    const links = rows.map((row, i) => {
      const y = i * ROW_H + ROW_H / 2;
      const active = row.value > 0 && total > 0;
      const share = total > 0 ? row.value / total : 0;
      const linkWidth = active
        ? Math.max(MIN_ACTIVE_W, (row.value / Math.max(maxValue, 1)) * MAX_LINK_W)
        : INACTIVE_W;
      const w = visualWeights[i];
      const band = (w / weightSum) * stackH;
      const sourceYLink = hubY + 4 + acc + band / 2;
      acc += band;

      return {
        ...row,
        y,
        active,
        percent: Math.round(share * 100),
        linkWidth,
        path: buildLinkPath(linkStartX, sourceYLink, linkEndX, y),
      };
    });

    return {
      height,
      hubX,
      hubY,
      links,
    };
  }, [rows, total, maxValue]);

  const handleHover = (key) => setHoveredKey(key);
  const handleLeave = () => setHoveredKey(null);

  return (
    <Card
      className={cn(
        "card-hover animate-fade-in flex h-full min-h-[28rem] min-w-0 flex-col overflow-hidden",
        delay,
        className,
      )}
      data-testid="dashboard-stat-manufacturers"
    >
      <CardHeader className="shrink-0 space-y-0 px-4 pb-1 pt-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">Implants</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-0">
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-10 text-sm text-muted-foreground">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-10 text-sm text-muted-foreground">No data yet</div>
        ) : (
          <div
            className={cn(
              "flex min-h-0 min-w-0 flex-1",
              fillParent ? "max-lg:min-h-[var(--mfr-min)]" : "min-h-[var(--mfr-min)]",
            )}
            style={{ "--mfr-min": `${layout.height}px` }}
          >
            <div className="relative min-h-0 min-w-0 flex-1">
              <svg
                viewBox={`0 0 ${SVG_W} ${layout.height}`}
                width="100%"
                height="100%"
                preserveAspectRatio="none"
                className="block"
                role="img"
                aria-label="Manufacturer patient flow"
              >
                <defs>
                  <linearGradient id="mfr-link-active" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={ACCENT} stopOpacity="0.95" />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity="0.28" />
                  </linearGradient>
                </defs>

                {layout.links.map((link, i) => {
                  const dimmed = hoveredKey && hoveredKey !== link.key;
                  return (
                    <path
                      key={link.key}
                      className="mfr-sankey-link"
                      d={link.path}
                      fill="none"
                      pathLength={1}
                      stroke={link.active ? "url(#mfr-link-active)" : INACTIVE}
                      strokeWidth={link.linkWidth}
                      strokeLinecap="round"
                      opacity={dimmed ? 0.18 : link.active ? 0.95 : 0.7}
                      style={{ animationDelay: `${80 + i * 55}ms` }}
                    />
                  );
                })}

                <rect
                  x={layout.hubX}
                  y={layout.hubY}
                  width={HUB_SIZE}
                  height={HUB_SIZE}
                  rx={9}
                  fill={ACCENT}
                />
              </svg>
              <div
                className="pointer-events-none absolute flex items-center justify-center text-white"
                style={{
                  left: `${(layout.hubX / SVG_W) * 100}%`,
                  top: `${(layout.hubY / layout.height) * 100}%`,
                  width: `${(HUB_SIZE / SVG_W) * 100}%`,
                  height: `${(HUB_SIZE / layout.height) * 100}%`,
                }}
              >
                <span className="text-xs font-bold leading-none tabular-nums sm:text-sm">
                  {total}
                </span>
              </div>
            </div>

            <ul className="flex h-full min-h-0 w-[9.75rem] shrink-0 flex-col overflow-hidden sm:w-[12.5rem]">
              {layout.links.map((link) => {
                const clickable = typeof onItemClick === "function";
                return (
                  <li key={link.key} className="min-h-0 flex-1">
                    <button
                      type="button"
                      disabled={!clickable}
                      className={cn(
                        "flex h-full min-h-0 w-full items-center justify-between gap-2 rounded-lg px-1.5 text-left sm:px-2",
                        clickable &&
                          "cursor-pointer transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      )}
                      data-testid={`dashboard-stat-item-${link.key}`}
                      onClick={() => onItemClick?.(link)}
                      onMouseEnter={() => handleHover(link.key)}
                      onMouseLeave={handleLeave}
                      onFocus={() => handleHover(link.key)}
                      onBlur={handleLeave}
                      aria-label={`View ${link.title} patients, ${link.value}`}
                    >
                      <span
                        className={cn(
                          "min-w-0 truncate text-[11px] font-medium sm:text-sm",
                          link.active ? "text-foreground" : "text-muted-foreground",
                        )}
                      >
                        {link.title}
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <span
                          className={cn(
                            "text-[11px] font-bold tabular-nums sm:text-sm",
                            link.active ? "text-foreground" : "text-muted-foreground",
                          )}
                        >
                          {link.value}
                        </span>
                        <span
                          className={cn(
                            "h-4 w-7 rounded-full sm:h-5 sm:w-8",
                            link.active
                              ? "bg-[var(--xl-primary)] shadow-[0_0_8px_var(--xl-primary-shadow)]"
                              : "bg-muted",
                          )}
                          aria-hidden
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ManufacturerSankeyCard;
